import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { ApiDigitalAuthAuthentication } from '../../services/auth/authentication.service';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Token } from '../../models/token.model';
import { legendIconInvalid, legendInvalid } from '../../utils/legend.data.util';
import { iconEye, iconEyeSlash } from '../../utils/icon.visibility.util';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { evaluatePasswordConfirmation, evaluatePasswordController } from '../../scripts/validate.password.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { appLanguageLocale } from '../../scripts/app.language.script';
import { authApiKey, authLogin } from '../../scripts/authentication.script';

import AppLegend from '../../components/element/Legend';

export interface RegisterInvitationPageProps {};

let errorResponse: Error, tokenResponse: Token;

const RegisterInvitationPage: React.FunctionComponent<RegisterInvitationPageProps> = props =>  {
  const {lang} = StateLanguage()
  const params = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [loadIndicator, setLoadIndicator] = useState('off')
  const [typeInput, setTypeInput] = useState('password')
  const [email, setEmail] = useState({value: '', valid: false})
  const [firstName, setFirstName] = useState({value: '', valid: false})
  const [lastName, setLastName] = useState({value: '', valid: false})
  const [phone, setPhone] = useState({value: '', valid: false})
  const [password, setPassword] = useState({value: '', valid: false})
  const [passwordConfirmation, setPasswordConfirmation] = useState({value: '', valid: false})

  const submitRegister = async (event: React.ChangeEvent <HTMLFormElement>) => {
    event.preventDefault()
    setLoadIndicator('on')

    if (params.token && email.valid && firstName.valid && lastName.valid && phone.valid && password.valid && passwordConfirmation.valid) {
      await ApiDigitalAuthAuthentication.registerInvitation(authApiKey(), params.token, email.value, firstName.value, lastName.value, phone.value, password.value, passwordConfirmation.value, appLanguageLocale()).then( (response) => {
        if (response.status === 201) {
          tokenResponse = response.data
          authLogin(tokenResponse)

          GlobalSwal.fire({
            title: lang.labels.successfulllyCreatedUser,
            text: lang.labels.yuouCanNowStartUsingApp,
            icon: 'success',
            confirmButtonText: lang.labels.okGotIt
          }).then( () => {
            navigate('/main/welcome', {replace: true})
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!email.value) legendIconInvalid('input-email', 'legend-email-required')
        if (!firstName.value) legendIconInvalid('input-first-name', 'legend-first-name-required')
        if (!lastName.value) legendIconInvalid('input-last-name', 'legend-last-name-required')
        if (!phone.value) legendIconInvalid('input-phone', 'legend-phone-required')
        if (!password.value) legendInvalid('legend-password-required')
        if (!passwordConfirmation.value) legendIconInvalid('input-password-confirmation', 'legend-confirmation-password-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeEmail = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setEmail({...email, value: event.target.value})
  }

  const handleChangeFirstName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setFirstName({...firstName, value: event.target.value})
  }

  const handleChangeLastName = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setLastName({...lastName, value: event.target.value})
  }

  const handleChangePhone = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPhone({...phone, value: event.target.value})
  }

  const handleChangePassword = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPassword({ ...password, value: event.target.value })
  }

  const handleChangePasswordConfirmation = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setPasswordConfirmation({...passwordConfirmation , value: event.target.value})
  }

  const handleChangeVisibilityPassword = () => {
    if (typeInput === 'password') {
      setTypeInput('text')
      iconEye('icon-password-visibility')
    } else {
      setTypeInput('password')
      iconEyeSlash('icon-password-visibility')
    }
  }

  const validateEmail = () => {
    evaluateLegendRequiredIcon(expressions.email, email, setEmail, 'input-email', 'legend-email-valid', 'legend-email-required')
  }

  const validateFirstName = () => {
    evaluateLegendRequiredIcon(expressions.user, firstName, setFirstName, 'input-first-name', 'legend-first-name-valid', 'legend-first-name-required')
  }

  const validateLastName = () => {
    evaluateLegendRequiredIcon(expressions.user, lastName, setLastName, 'input-last-name', 'legend-last-name-valid', 'legend-last-name-required')
  }

  const validatePhone = () => {
    evaluateLegendRequiredIcon(expressions.phone, phone, setPhone, 'input-phone', 'legend-phone-valid', 'legend-phone-required')
  }

  const validatePassword = () => {
    evaluatePasswordController(password, setPassword, 'legend-password-valid', 'legend-password-required', 'container-password-meter-control-1', 'container-password-meter-control-2', 'container-password-meter-control-3', 'container-password-meter-control-4', 'container-password-meter-control-5')
    evaluatePasswordConfirmation(true, password, passwordConfirmation, setPasswordConfirmation, 'input-password-confirmation', 'legend-confirmation-password-identical', 'legend-confirmation-password-valid', 'legend-confirmation-password-required')
  }

  const validatePasswordConfirmation = () => {
    evaluatePasswordConfirmation(false, password, passwordConfirmation, setPasswordConfirmation, 'input-password-confirmation', 'legend-confirmation-password-identical', 'legend-confirmation-password-valid', 'legend-confirmation-password-required')
  }

  useEffect( () => {
    setMounted(true)

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-lg-600px bg-body rounded shadow-sm p-10 mx-auto">
      <form className="form w-100" noValidate onSubmit={submitRegister}>
        <div className="text-center mb-10">
          <h1 className="text-dark text-capitalize mb-3">{lang.labels.createAnAccount}</h1>
          <div className="text-muted fw-bold fs-4">
            {lang.labels.alreadyHaveAnCccount}
            <Link to="/auth/login" className="link-primary fw-bolder ms-2">{lang.labels.signInHere}</Link>
          </div>
        </div>
        <div className="fv-row mb-5">
          <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.email}</label>
          <input id="input-email" className="form-control form-control-lg" type="email" autoComplete="off" name="email" value={email.value} onChange={handleChangeEmail} onKeyUp={validateEmail} onBlur={validateEmail} />
          <AppLegend attribute={{check: email.valid, name: "email", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="row fv-row mb-5">
          <div className="col-xl-6 mb-5 mb-xl-0">
            <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.firstName}</label>
            <input id="input-first-name" className="form-control form-control-lg" type="text" autoComplete="off" name="first-name" value={firstName.value} onChange={handleChangeFirstName} onKeyUp={validateFirstName} onBlur={validateFirstName} />
            <AppLegend attribute={{check: firstName.valid, name: "first-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
          </div>
          <div className="col-xl-6">
            <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.lastName}</label>
            <input id="input-last-name" className="form-control form-control-lg" type="text" autoComplete="off" name="last-name" value={lastName.value} onChange={handleChangeLastName} onKeyUp={validateLastName} onBlur={validateLastName} />
            <AppLegend attribute={{check: lastName.valid, name: "last-name", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
          </div>
        </div>
        <div className="fv-row mb-5">
          <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.phoneNumber}</label>
          <input id="input-phone" className="form-control form-control-lg" type="tel" autoComplete="off" name="phone" value={phone.value} onChange={handleChangePhone} onKeyUp={validatePhone} onBlur={validatePhone} />
          <AppLegend attribute={{check: phone.valid, name: "phone", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="fv-row mb-5">
          <div className="mb-0">
            <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.password}</label>
            <div className="position-relative mb-2">
              <input id="input-password" className="form-control form-control-lg" name="password" autoComplete="new-password" type={typeInput} value={password.value} onChange={handleChangePassword} onKeyUp={validatePassword} onBlur={validatePassword} />
              <span className="btn btn-sm btn-icon position-absolute translate-middle top-50 end-0 me-n2">
                <i id="icon-password-visibility" className="bi bi-eye-slash fs-2" onClick={handleChangeVisibilityPassword}></i>
              </span>
            </div>
            <div className="d-flex align-items-center mb-2">
              <div id="container-password-meter-control-1" className="flex-grow-1 bg-secondary bg-active-info rounded h-5px me-2"></div>
              <div id="container-password-meter-control-2" className="flex-grow-1 bg-secondary bg-active-info rounded h-5px me-2"></div>
              <div id="container-password-meter-control-3" className="flex-grow-1 bg-secondary bg-active-info rounded h-5px me-2"></div>
              <div id="container-password-meter-control-4" className="flex-grow-1 bg-secondary bg-active-info rounded h-5px me-2"></div>
              <div id="container-password-meter-control-5" className="flex-grow-1 bg-secondary bg-active-info rounded h-5px"></div>
            </div>
          </div>
          <div className="form-text text-justify">{lang.labels.use8OrMoreCharacters}</div>
          <AppLegend attribute={{check: password.valid, name: "password", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
        </div>
        <div className="fv-row mb-10">
          <label className="form-label fs-6 fw-bolder text-dark">{lang.labels.confirmPassword}</label>
          <input id="input-password-confirmation" className="form-control form-control-lg" type="password" autoComplete="new-password" name="password-confirmation" value={passwordConfirmation.value} onChange={handleChangePasswordConfirmation} onKeyUp={validatePasswordConfirmation} onBlur={validatePasswordConfirmation} />
          <AppLegend attribute={{check: passwordConfirmation.valid, name: "confirmation-password", index: null, sub_index: null}} container={{valid: true, required: true, identical: true, exists: false}}></AppLegend>
        </div>
        <div className="text-center">
          <button className="btn btn-lg btn-primary" type="submit" data-kt-indicator={loadIndicator}>
            <span className="indicator-label fw-bolder">{lang.labels.submit}</span>
            <span className="indicator-progress">
              {lang.labels.pleaseWait}
              <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
            </span>
          </button>
        </div>
      </form>
    </div>
  )
};

export default RegisterInvitationPage;
